import { DashboardIcon } from "@radix-ui/react-icons";
import { Link, useLocationChange } from "raviger";
import * as React from "react";
import { useTranslation } from "react-i18next";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { AdminNav } from "@/components/ui/sidebar/admin-nav";
import { FacilityNav } from "@/components/ui/sidebar/facility/facility-nav";
import { LocationNav } from "@/components/ui/sidebar/facility/location/location-nav";
import { LocationSwitcher } from "@/components/ui/sidebar/facility/location/location-switcher";
import { ServiceNav } from "@/components/ui/sidebar/facility/service/service-nav";
import {
  FacilityNavUser,
  PatientNavUser,
} from "@/components/ui/sidebar/nav-user";
import { OrgNav } from "@/components/ui/sidebar/org-nav";
import { OrganizationSwitcher } from "@/components/ui/sidebar/organization-switcher";
import { PatientNav } from "@/components/ui/sidebar/patient-nav";

import { useRouteParams } from "@/hooks/useRouteParams";
import { ServiceSwitcher } from "./facility/service/service-switcher";

import { Avatar } from "@/components/Common/Avatar";
import { ThemeToggle } from "@/components/Common/ThemeToggle";
import { FacilityBareMinimum } from "@/types/facility/facility";
import { CurrentUserRead } from "@/types/user/user";
import { Building2, ChevronDown, ChevronRight } from "lucide-react";

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user?: CurrentUserRead;
  facilitySidebar?: boolean;
  sidebarFor?: SidebarFor;
}

export enum SidebarFor {
  FACILITY = "facility",
  PATIENT = "patient",
  ADMIN = "admin",
}

export function AppSidebar({
  user,
  sidebarFor = SidebarFor.FACILITY,
  ...props
}: AppSidebarProps) {
  const { t } = useTranslation();

  const { facilityId } = useRouteParams("/facility/:facilityId");
  const { locationId } = useRouteParams("/facility/:_/locations/:locationId");
  const { organizationId } = useRouteParams("/organization/:organizationId");
  const { serviceId } = useRouteParams(
    "/facility/:facilityId/services/:serviceId",
  );
  const adminDashboardRoute = location.pathname.includes("/admin");
  const facilitySidebar =
    !!facilityId &&
    !locationId &&
    !serviceId &&
    sidebarFor === SidebarFor.FACILITY;
  const facilityLocationSidebar =
    !!facilityId &&
    !!locationId &&
    !serviceId &&
    sidebarFor === SidebarFor.FACILITY;
  const facilityServiceSidebar =
    !!facilityId &&
    !!serviceId &&
    !locationId &&
    sidebarFor === SidebarFor.FACILITY;

  const patientSidebar = sidebarFor === SidebarFor.PATIENT;
  const adminSidebar = !!adminDashboardRoute && sidebarFor === SidebarFor.ADMIN;

  const { isMobile, setOpenMobile, state } = useSidebar();

  React.useEffect(() => {
    if (state === "collapsed") {
      setIsFacilityOpen(false);
    }
  }, [state]);

  const [selectedFacility, setSelectedFacility] =
    React.useState<FacilityBareMinimum | null>(null);

  const selectedOrganization = React.useMemo(() => {
    if (!user?.organizations || !organizationId) return undefined;
    return user.organizations.find((org) => org.id === organizationId);
  }, [user?.organizations, organizationId]);

  React.useEffect(() => {
    if (!user?.facilities || !facilityId || !facilitySidebar) {
      setSelectedFacility(null);
      return;
    }

    const facility = user.facilities.find((f) => f.id === facilityId) || null;
    setSelectedFacility(facility);
  }, [facilityId, user?.facilities, facilitySidebar]);

  const hasFacilities = user?.facilities && user.facilities.length > 0;
  const hasOrganizations = user?.organizations && user.organizations.length > 0;

  useLocationChange(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  });

  const [isFacilityOpen, setIsFacilityOpen] = React.useState(false);

  return (
    <Sidebar
      collapsible="icon"
      variant="sidebar"
      {...props}
      className="group-data-[side=left]:border-r-0"
    >
      <SidebarHeader>
        {selectedOrganization && hasOrganizations && (
          <OrganizationSwitcher
            organizations={user?.organizations || []}
            selectedOrganization={selectedOrganization}
          />
        )}
        {hasFacilities && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setIsFacilityOpen((prev) => !prev)}
                className="justify-between"
                tooltip={
                  selectedFacility
                    ? `${selectedFacility.name}`
                    : t("facilities")
                }
              >
                <div className="flex items-center gap-2 leading-none">
                  <Building2 size={18} />

                  <span className="text-xs font-medium text-gray-500 uppercase group-data-[collapsible=icon]:hidden">
                    {t("facilities")}
                  </span>
                </div>

                {isFacilityOpen ? (
                  <ChevronDown size={16} className="ml-auto" />
                ) : (
                  <ChevronRight size={16} className="ml-auto" />
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>

            {isFacilityOpen && (
              <>
                {selectedFacility && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive
                      tooltip={selectedFacility.name}
                    >
                      <Link href={`/facility/${selectedFacility.id}/overview`}>
                        <div
                          className="
                          flex items-center w-full
                          justify-start gap-2
                          group-data-[collapsible=icon]:justify-center
                          "
                        >
                          <div className="hidden group-data-[collapsible=icon]:flex items-center justify-center size-10">
                            <Avatar
                              name={selectedFacility.name}
                              className="size-6 text-xs"
                            />
                          </div>

                          <span className="group-data-[collapsible=icon]:hidden">
                            {selectedFacility.name}
                          </span>
                        </div>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                {user?.facilities
                  ?.filter((f) => f.id !== selectedFacility?.id)
                  .map((facility) => (
                    <SidebarMenuItem key={facility.id}>
                      <SidebarMenuButton
                        asChild
                        tooltip={facility.name}
                        isActive={facility.id === selectedFacility?.id}
                      >
                        <Link href={`/facility/${facility.id}/overview`}>
                          <div
                            className="
                            flex items-center w-full
                            justify-start gap-2
                            group-data-[collapsible=icon]:justify-center
                            "
                          >
                            <div className="hidden group-data-[collapsible=icon]:flex items-center justify-center size-10">
                              <Avatar
                                name={facility.name}
                                className="size-6 text-xs"
                              />
                            </div>

                            <span className="group-data-[collapsible=icon]:hidden">
                              {facility.name}
                            </span>
                          </div>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
              </>
            )}
          </SidebarMenu>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t("view_dashboard")}>
              <Link href="/">
                <div className="flex items-center gap-2">
                  <DashboardIcon className="size-4" />

                  <span className="group-data-[collapsible=icon]:hidden">
                    {t("view_dashboard")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {locationId && <LocationSwitcher />}
        {serviceId && <ServiceSwitcher />}
        {!locationId &&
          !serviceId &&
          !selectedFacility &&
          !selectedOrganization &&
          !adminDashboardRoute &&
          !patientSidebar && (
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  size="lg"
                  className="!w-10 !h-10 !p-0 flex items-center justify-center hover:bg-gray-100 mt-2 rounded-xl"
                  title={t("view_dashboard")}
                >
                  <Link href="/">
                    <div className="flex aspect-square size-10 items-center justify-center rounded-xl bg-[#0F4C75] text-white">
                      <DashboardIcon className="size-5" />
                    </div>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          )}
      </SidebarHeader>

      <SidebarContent>
        {facilityLocationSidebar && <LocationNav />}
        {facilityServiceSidebar && <ServiceNav />}
        {facilitySidebar &&
          !facilityLocationSidebar &&
          !facilityServiceSidebar &&
          !selectedOrganization && (
            <FacilityNav selectedFacility={selectedFacility} />
          )}
        {selectedOrganization && (
          <OrgNav organizations={user?.organizations || []} />
        )}
        {patientSidebar && <PatientNav />}
        {adminSidebar && <AdminNav />}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-2 px-2">
              <ThemeToggle />
              <span className="text-sm group-data-[collapsible=icon]:hidden">
                Theme
              </span>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
        {patientSidebar ? (
          <PatientNavUser />
        ) : (
          <FacilityNavUser
            selectedFacilityId={
              facilitySidebar ? selectedFacility?.id : undefined
            }
          />
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
